<template>
  <a v-on:click.prevent.stop="handleClick" v-bind:href="localBookingLink" title="Click to book room now"
     class="btn btn-book-now btn-highlight" :class="{'btn-disabled': $store.state.bookingDisabled}">PROCEED TO BOOK</a>
</template>

<script lang="ts">
/*!
 * Copyright (C) 2019-2023, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *		2022-06-23
 *			- <view-room-details-book-button> now uses Vuex getter for "showingPreferredOffer"
 *
 *		2022-09-22
 *			- <view-room-details-book-button> now uses port 4323 for ResNet API calls.
 *
 *		2023-07-13
 *			- <view-room-details-book-button> sets both UA and GA4 linkers.
 *
 *		2024-07-13
 *			- Code improvements and bug fixes in <view-room-details-book-button>.
*/

import {defineComponent} from 'vue';
import {mapGetters, mapState} from 'vuex';
import {CheckRoomTypeAvailableServerResponse, ServerResponse} from "../../defs/serverResponse";
import {ViewRoomDetailsContainerComponent} from "./container.vue";

export default defineComponent({
  name: "view-room-details-book-button",
  props: {
    preferredSavings: {
      type: String,
      default: ''
    },
    reservationTotal: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      loading: false,
      confirmedIata: ''
    };
  },
  computed: {
    ...mapGetters([
      'showingPreferredOffer',
      'bookingLink',
      'currentRoomType'
    ]),
    ...mapState([
      'bookingDisabled',
      'bookingLinkData',
      'showPreferredOnboardingForm',
      'preferredUserIsLoggedIn',
      'metaClick'
    ]),
    showPreferredDialog() {
      return (this.$store.state.showPreferredOnboardingForm && !this.$store.state.preferredUserIsLoggedIn);
    },
    localBookingLink(): string {
      if (this.$store.getters.currentRoomType) {
        return this.$store.getters.bookingLink(this.$store.getters.currentRoomType, this.$store.getters.currentRoomType.PRT_ID, this.$store.state.bookingLinkData.offerCode, this.$store.getters.currentRoomType.PAGE_ID)
      }

      return '#';
    }
  },
  methods: {
    dailyRates() {
      const currentRoomType = this.$store.getters.currentRoomType;
      let output = [];
      if (currentRoomType) {
        for (let i = 0, l = currentRoomType.DATA.length; i < l; i++) {
          output.push({
            price: (true === this.$store.getters.showingPreferredOffer && 'preferred_occupancy_rates' in currentRoomType.DATA[i] && currentRoomType.DATA[i].preferred_occupancy_rates[this.$store.state.bookingLinkData.adults] > 0)
              ? '$' + window.pepHelperFunctions.formatAsDollarAmount(currentRoomType.DATA[i].preferred_occupancy_rates[this.$store.state.bookingLinkData.adults], false)
              : '$' + window.pepHelperFunctions.formatAsDollarAmount(currentRoomType.DATA[i].occupancy_rates[this.$store.state.bookingLinkData.adults], false),
            date: currentRoomType.DATA[i].rrcd_rate_date,
          });
        }
      }
      return output;
    },
    redirectToBooking(url: string) {
      $('html')
        .addClass('redirecting-booking');
      if (!/[?&]_ga=.+/.test(url)) {
        url += ((-1 === url.indexOf('?')) ? '?' : '&') + '_ga=' + window.pepTracking.getGALinkerParam();
      }
      if (!/[?&]_gl=.+/.test(url)) {
        url += ((-1 === url.indexOf('?')) ? '?' : '&') + '_gl=' + window.pepTracking.getGA4LinkerParam();
      }
      if (this.$store.state.metaClick && !/[?&]tag_meta=YES/.test(url)) {
        url += ((-1 === url.indexOf('?')) ? '?' : '&') + 'tag_meta=YES';
      }
      window.location.href = url;
    },
    handleClick() {

      if (this.$store.state.bookingDisabled) {
        return;
      }

      if (this.showPreferredDialog) {

        window.pepConsole.log('handleClick show loader');
        window.showLoader(false);

        let bodyCopy = 'You are not currently signed in with a free Peppermill Preferred account and will not receive the membership rate.';

        if ('' !== this.preferredSavings) {
          bodyCopy = 'Sign up for free or login now to save <strong>' + this.preferredSavings + '</strong> or continue without saving.';
        }
        bodyCopy += '</p><p>Your total will be ' + this.reservationTotal + '.';

        window.ajaxLinkRegisterCallback(
          'PREFERRED-CONTINUE-WITHOUT-LOGIN',
          () => {
            this.redirectToBooking(this.localBookingLink);
          }
        );
        window.ajaxLinkRegisterCallback(
          'PREFERRED-LOGIN-NOW',
          () => {

            //@ts-ignore
            const signupFormOffset = $('.pepvalidate-v3').filter('.signup-preferred').offset().top;
            const scrollTop = $(window).scrollTop() || 0;
            const headerHeight = $('#header').outerHeight() || 0;
            const offsetBuffer = 24;

            if (signupFormOffset < scrollTop + headerHeight + offsetBuffer) {

              $('html, body')
                .animate({scrollTop: signupFormOffset - headerHeight - offsetBuffer}, 250, 'easeInOutCubic');

            }

          }
        );

        window.showOverlayDialog({
          alertMode: false,
          confirmAccept: 'CONTINUE',
          confirmDeny: 'SIGN UP/LOGIN',
          confirmTitle: 'Are You Sure?',
          confirmBody: bodyCopy,
          callback: 'PREFERRED-CONTINUE-WITHOUT-LOGIN',
          callbackDeny: 'PREFERRED-LOGIN-NOW'
        });

      } else {
        window.ajaxLinkRegisterCallback(
          'RATE-VARIANCES-CONTINUE-BOOKING',
          //@ts-ignore
          (url: string) => {
            this.redirectToBooking(url);
          }
        );
        this.checkRoomTypeAvailable((response: CheckRoomTypeAvailableServerResponse) =>  {

          if ('SUCCESS' === response.STATUS && 'URL' in response.DATA) {
            //show user rate variances if they exist, otherwise send them on their way
            if ('RATE_VARIATION' in response.DATA && 'YES' === response.DATA.RATE_VARIATION) {
              let variationHtml = '<form class="pepvalidate-v3"><input name="url" type="hidden" value="' + response.DATA.URL + '" /></form>';
              if ('RATE_VARIATION_DATA' in response.DATA) {
                variationHtml += '<table><thead><tr><th>Date</th><th>Variation</th></tr></thead><tbody>';
                response.DATA.RATE_VARIATION_DATA?.forEach(function (data) {
                  variationHtml += '<tr><td>' + data.DATE_DISPLAY + '</td><td>' + data.RATE_DIFF_DISPLAY + '</td></tr>';
                });
                if (1 < (response.DATA.RATE_VARIATION_DATA?.length || 0) && '' !== response.DATA.RATE_VARIATION_TOTAL) {
                  variationHtml += '<tr><td style="color:rgba(0,0,0,0.54);text-align:right;">Total Difference</td><td>' + response.DATA.RATE_VARIATION_TOTAL + '</td></tr>';
                }
                variationHtml += '</tbody></table><p>If you approve click "CONTINUE" to book now.';
              }
              window.showOverlayDialog({
                alertMode: false,
                confirmAccept: 'CONTINUE',
                confirmDeny: 'CANCEL',
                confirmTitle: 'Rate Variances',
                confirmBody: 'We have found some rate variances since you loaded this page.</p>' + variationHtml,
                callback: 'RATE-VARIANCES-CONTINUE-BOOKING',
                callbackDeny: ''
              });
            } else {
              this.redirectToBooking(response.DATA.URL);
            }

          } else if ('ERROR' === response.STATUS) {

            window.ajaxLinkRegisterCallback(
              'SHOW-SIMILAR-ROOMS',
              () => {
                this.$emit('update:showSimilarRooms', true);
              }
            );

            window.ajaxLinkRegisterCallback(
              'SHOW-DATE-PICKER',
              () => {

                (this.$parent as ViewRoomDetailsContainerComponent | null)?.openCalendar(true, true);

                window.hideLoader();

                const calendar = document.querySelector('.check-in-out-calendar');
                const header = document.querySelector('#header');

                if (calendar) {
                  let scroll = window.scrollY + calendar.getBoundingClientRect().top;
                  if (header) {
                    scroll -= header.clientHeight;
                  }
                  window.scrollTo({
                    top: scroll,
                    behavior: 'smooth'
                  });
                }

              }
            );

            if ('BOXED_DATES' in response.DATA && 'YES' === response.DATA.BOXED_DATES) {
              window.showOverlayDialog({
                alertMode: false,
                confirmAccept: 'CHANGE DATES',
                confirmDeny: 'OK',
                confirmTitle: 'We\'re Sorry',
                confirmBody: 'There are no longer any rooms available for at least one of the nights you selected.</p><p>Try changing dates if your plans allow.',
                callback: 'SHOW-DATE-PICKER'
              }, this.$el as HTMLElement);

              if ('window.pepTracking' in window) {
                window.pepTracking.event('View Room Details', 'Room No Longer Available (Boxed)', 'prt_id: ' + this.$store.state.bookingLinkData.prt_id, 0, true);
              }

            } else if ('OVERBOOK_CONTROL' in response.DATA && 'YES' === response.DATA.OVERBOOK_CONTROL) {

              this.$emit('update:similarRoomTowerOmit', (('ROOM_TOWER' in response.DATA) ? response.DATA.ROOM_TOWER : ''));

              window.showOverlayDialog({
                alertMode: false,
                confirmAccept: 'VIEW SIMILAR',
                confirmDeny: 'OK',
                confirmTitle: 'We\'re Sorry',
                confirmBody: 'The room and tower you selected is no longer available.</p><p>Click "View Similar" to see options in other towers that might work for you.',
                callback: 'SHOW-SIMILAR-ROOMS'
              }, this.$el as HTMLElement);

              if ('window.pepTracking' in window) {
                window.pepTracking.event('View Room Details', 'Room No Longer Available (Overbook)', 'prt_id: ' + this.$store.state.bookingLinkData.prt_id, 0, true);
              }
            } else {

              window.showOverlayDialog({
                alertMode: false,
                confirmAccept: 'VIEW SIMILAR',
                confirmDeny: 'CANCEL',
                confirmTitle: 'We\'re Sorry',
                confirmBody: 'The room you selected is no longer available.</p><p>Click "View Similar" to see other options that might work for you.',
                callback: 'SHOW-SIMILAR-ROOMS'
              }, this.$el as HTMLElement);

              if ('window.pepTracking' in window) {
                window.pepTracking.event('View Room Details', 'Room No Longer Available (Inventory)', 'prt_id: ' + this.$store.state.bookingLinkData.prt_id, 0, true);
              }
            }
          } else {
            window.location.href = this.$store.getters.bookingLink(this.$store.state.roomData);
          }

        }, () => {
          window.showOverlayDialog({
            alertMode: true,
            confirmAccept: 'OK',
            confirmTitle: 'Oh No!',
            confirmBody: 'An error occurred. Please try again in a couple minutes or give us a call at 1-866-821-9996',
          }, this.$el as HTMLElement);
        }, false);
      }
    },


    checkRoomTypeAvailable(
      successCallback: (response: CheckRoomTypeAvailableServerResponse) => void,
      errorCallback: (response: {STATUS: string, ERRORS: string[]}) => void,
      runInBackground = false
    ) {
      const requestData = {...this.$store.state.bookingLinkData};
      // var roomConfirmed	=	false;
      // var iataConfirmed	=	false;

      requestData.ACTION = 'CHECK_ROOM_TYPE_AVAILABLE';
      requestData.RATE_DATA = this.dailyRates();

      const runCheck = () => {

        $.ajax({
          url: '/library/api/resnet.php',
          type: 'post',
          dataType: 'json',
          data: requestData,
          success: (response) => {
            this.loading = false;
            successCallback.call(this, response);
          },
          error: (jqXHR, textStatus, errorThrown) => {
            this.loading = false;

            if (!runInBackground) {
              window.window.hideLoader(false, true);
            }

            //log this error
            const extraText = (typeof errorThrown === 'string')
              ? ' -- ' + errorThrown
              : '';
            window.pepTracking.event('JS Error', 'Booking Link Check Availability', textStatus + extraText, 0, true);

            $.ajax({
              url: '/library/api/capture-js-error.php',
              dataType: 'json',
              type: 'post',
              data: {
                je_message: 'Booking Link Check Avaialbility',
                je_source: 'https://www.peppermillreno.com/library/js/view-room-details.min.js',
                je_lineno: 0,
                je_colno: 0,
                je_url: window.location.href,
                je_stack: "Status: " + jqXHR.status + "\n\nResponse Text:\n" + jqXHR.responseText
              }
            });

            errorCallback.call(this, {STATUS: 'ERROR', ERRORS: ['AJAX_ERROR']});

          }
        });
      }

      /*if (!$btn.length) {

        errorCallback({STATUS:'ERROR',ERRORS:['NO_BTN']});

        return false;

      } else */
      if (this.loading && !runInBackground) {

        errorCallback({STATUS: 'ERROR', ERRORS: ['LOADING']});
        return false;

      } else if (!requestData) {

        errorCallback({STATUS: 'ERROR', ERRORS: ['NO_REQUEST_DATA']});
        return false;

      }

      if (!runInBackground) {

        this.loading = true;
        window.pepConsole.log('checkRoomTypeAvailable show loader');
        window.showLoader(true);

      }

      if ('' === this.$store.state.bookingLinkData.iata || this.confirmedIata === this.$store.state.bookingLinkData.iata) {

        //no IATA to verify or it's already been verified
        runCheck();

      } else {

        //need to verify IATA before verifying that the room type is still available
        $.ajax({
          url: ('dev.peppermillreno.com' === location.hostname && $('html').hasClass('on-corporate'))
            ? 'https://chili.peppermillcas.com:4323/resnet-api/index.php'
            : 'https://reserve1.peppermillcas.com:4323/resnet-api/index.php',
          type: 'post',
          dataType: 'json',
          data: {
            ACTION: 'VERIFY_TRAVEL_AGENT',
            IATA: this.$store.state.bookingLinkData.iata
          },
          success(response: ServerResponse) {

            if ('SUCCESS' === response.STATUS) {

              this.confirmedIata = this.$store.state.bookingLinkData.iata;
              runCheck();

            } else {

              this.loading = false;
              window.showOverlayDialog({
                alertMode: true,
                confirmAccept: 'CLOSE',
                confirmTitle: 'Invalid IATA',
                confirmBody: 'We could not verify your IATA. Please make sure it has been entered correctly.'
              });
            }
          },
          error(jqXHR, textStatus, errorThrown) {

            if (!runInBackground) {
              this.loading = false;
              window.window.hideLoader(false, true);
            }

            //log this error
            const extraText = (typeof errorThrown === 'string')
              ? ' -- ' + errorThrown
              : '';
            window.pepTracking.event('JS Error', 'Booking Link Check IATA', textStatus + extraText, 0, true);

            $.ajax({
              url: '/library/api/capture-js-error.php',
              dataType: 'json',
              type: 'post',
              data: {
                je_message: 'Booking Link Check IATA',
                je_source: 'https://www.peppermillreno.com/library/js/view-room-details.min.js',
                je_lineno: 0,
                je_colno: 0,
                je_url: window.location.href,
                je_stack: "Status: " + jqXHR.status + "\n\nResponse Text:\n" + jqXHR.responseText
              }
            });

            errorCallback.call(this, {STATUS: 'ERROR', ERRORS: ['AJAX_ERROR_IATA']});

          }
        });

      }

    }
  }
})
</script>

<style scoped>

</style>